// Catalog.ID Client — Kit Page Layout (pure)
// ============================================================================
// The kit is printed as ONE document with ONE run of page numbers, even though
// it is produced by two different renderers: pdfmake draws the cover, the
// introduction and the sheets, and the pre-rendered agreement is appended
// afterwards with pdf-lib (see kit-merge.ts). Everything that has to agree on
// a page number — the footer's "of N", the introduction's table of contents,
// and the introduction's consent statement — reads it from here, so nothing is
// hard-coded and nothing can drift.
//
// Which agreement rides a kit depends on the kit (round 5, 2026-09-03): the
// personal kit carries the Membership Agreement, the organization kit the
// Organization Identity Agreement, the delegate kit the Delegate Terms — so
// the layout is told the agreement's TITLE along with its page count.
//
// ASSUMPTION (load-bearing): every master page handed to the assembler is
// exactly ONE physical A4 page. The page builders are budgeted for that (see
// the PAGE BUDGET note in recovery-card-page.ts) and the assembler puts a
// pageBreak before each one, so a builder that overflows would silently shift
// every number after it. The sample renderer exists to catch that by eye.
//
// The extra copy sheets are deliberately OUTSIDE the numbering: they ride after
// the sheets with their own footer line, so "page 4 of 15" keeps meaning what
// it said when the owner counted the pages. Since 2026-09-08 the contents list
// names them anyway — one row per copy, a dash where the number would be — so
// the layout carries their titles (`copies`), in print order.
//
// Recovery sheets are one section EACH (2026-09-08, product owner's review):
// "Account Recovery Sheet B, page 7", not "Sheets A–C, pages 6–8". The two
// helpers at the bottom build those sections and the copy titles from the kit,
// so the four callers (both wizards, the sample renderer, the fixture
// generator) cannot drift from the sheet pages' own titles and footers.

import { recoverySheetCopyTitle, recoverySheetTitle } from './shared';

export const MEMBERSHIP_AGREEMENT_TITLE = 'Catalog.ID Membership Agreement';

/** One run of consecutive physical pages that the table of contents names as a
 *  single line — "Account Recovery Sheet B", page 7. */
export interface KitSectionSpec {
    title: string;
    /** Physical A4 pages this section occupies. Must be ≥ 1. */
    pages: number;
    /** Default true. The cover is page 1 but does not list itself. */
    inToc?: boolean;
}

/** The agreement appended to the kit at print time. */
export interface KitTermsSpec {
    /** Printed title, e.g. 'Catalog.ID Membership Agreement' — what the table
     *  of contents and the consent statement call it. */
    title: string;
    /** Pages of the pre-rendered agreement PDF. Must be ≥ 1. */
    pages: number;
}

export interface KitLayoutSpec {
    /** The master pages in print order, cover first. */
    sections: KitSectionSpec[];
    /** Extra recovery-sheet copies riding after the master pages, unnumbered:
     *  their footer titles in print order — see `recoveryCopyTitles`. */
    copySheets?: string[];
    /** The appended agreement; absent = a kit without terms (a standalone
     *  Settings reprint). */
    terms?: KitTermsSpec;
}

export interface KitTocEntry {
    title: string;
    firstPage: number;
    lastPage: number;
    /** "3" or "5–7" — what the table of contents prints. */
    range: string;
}

export interface KitLayout {
    /** pdfmake master pages: cover … last recovery sheet. */
    masterCount: number;
    /** Unnumbered extra copies printed after them. */
    copySheetCount: number;
    /** Their titles, in print order — the contents list's copy rows. */
    copies: string[];
    termsPages: number;
    /** masterCount + termsPages — the "of N" every footer prints. */
    totalPages: number;
    toc: KitTocEntry[];
    /** Where the agreement landed, for the consent copy. */
    terms: KitTocEntry | null;
}

export function buildKitLayout(spec: KitLayoutSpec): KitLayout {
    const toc: KitTocEntry[] = [];
    let page = 1;
    for (const section of spec.sections) {
        if (!Number.isInteger(section.pages) || section.pages < 1) {
            throw new Error(`kit section "${section.title}" must occupy at least one page`);
        }
        const firstPage = page;
        page += section.pages;
        if (section.inToc !== false) toc.push(entry(section.title, firstPage, page - 1));
    }
    const masterCount = page - 1;

    const termsPages = spec.terms?.pages ?? 0;
    const terms = spec.terms && termsPages > 0
        ? entry(spec.terms.title, masterCount + 1, masterCount + termsPages)
        : null;
    if (terms) toc.push(terms);

    const copies = spec.copySheets ?? [];
    return {
        masterCount,
        copySheetCount: copies.length,
        copies,
        termsPages,
        totalPages: masterCount + termsPages,
        toc,
        terms,
    };
}

/** The shape of a recovery kit the layout needs: share indexes and copy
 *  counts. `RecoveryKit` (crypto) and `RecoveryCardPagesKit` both fit. */
export interface KitRecoverySheets {
    cards: { payload: { shareIndex: number }; extraCopies?: number }[];
}

/** One section per recovery sheet, in share order, titled as the sheet page
 *  titles itself. */
export function recoverySheetSections(kit: KitRecoverySheets): KitSectionSpec[] {
    return kit.cards.map(card => ({ title: recoverySheetTitle(card.payload.shareIndex), pages: 1 }));
}

/** The extra copies' titles in PRINT order — sheet by sheet, copy by copy —
 *  exactly the footers `buildRecoveryCardCopySheets` prints. */
export function recoveryCopyTitles(kit: KitRecoverySheets): string[] {
    const titles: string[] = [];
    for (const card of kit.cards) {
        const extra = card.extraCopies ?? 0;
        for (let index = 1; index <= extra; index++) {
            titles.push(recoverySheetCopyTitle(card.payload.shareIndex, index, extra));
        }
    }
    return titles;
}

function entry(title: string, firstPage: number, lastPage: number): KitTocEntry {
    return { title, firstPage, lastPage, range: formatPageRange(firstPage, lastPage) };
}

/** "3" for one page, "5–7" (en dash) for a span. */
export function formatPageRange(firstPage: number, lastPage: number): string {
    return firstPage === lastPage ? `${firstPage}` : `${firstPage}–${lastPage}`;
}

/** "page 3" / "pages 8–15" — the range in running prose. */
export function describePages(firstPage: number, lastPage: number): string {
    return `${firstPage === lastPage ? 'page' : 'pages'} ${formatPageRange(firstPage, lastPage)}`;
}

/** The introduction's agreement statement — the kit's ONE consent statement
 *  (round 5, 2026-09-03: the per-sheet consent lines are gone; this carries
 *  the whole weight, so the front page sets it larger). Null when the kit
 *  carries no terms (a standalone reprint). */
export function kitConsentStatement(layout: KitLayout): string | null {
    if (!layout.terms) return null;
    return `By activating your account using the codes in this kit, you agree to the `
        + `${layout.terms.title}, ${describePages(layout.terms.firstPage, layout.terms.lastPage)} `
        + `of this document.`;
}
